import React, { useCallback, useMemo } from 'react';

import { useParams } from 'react-router-dom';

import useFeatureFlags from 'api/useFeatureFlags';
import { EFeatureFlag } from 'constants/enums';

import { IVersionConfig } from '../DeveloperSettings.types';

import { SettingsCard } from './SettingsCard';
import { VersionRow } from './VersionRow';

export interface ICallbackSectionProps {
  title: string;
  description?: string;
  versionConfigs: IVersionConfig[];
  onSave: () => void;
  isSaveDisabled?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  onTestClick?: () => void;
  testButtonDisabled?: boolean;
  children?: React.ReactNode;
}

const isVersionSelectable = (config: IVersionConfig): boolean => {
  return config.isSelected !== undefined;
};

export const CallbackSection: React.FC<ICallbackSectionProps> = ({
  title,
  description,
  versionConfigs,
  onSave,
  isSaveDisabled,
  isLoading,
  disabled,
  onTestClick,
  testButtonDisabled,
  children
}) => {
  const { publisherId } = useParams<{ publisherId: string }>();
  const { featureFlags } = useFeatureFlags({ publisherId });
  const shouldShowAllVersions =
    featureFlags?.[EFeatureFlag.DASHBOARD_DISPLAY_INTEGRATION_OLD_VERSIONS];

  const latestVersion = useMemo(
    () => versionConfigs[versionConfigs.length - 1]?.version,
    [versionConfigs]
  );

  const visibleConfigs: IVersionConfig[] = useMemo(() => {
    const isVersionVisible = (
      config: IVersionConfig,
      latestVersion: string | undefined
    ): boolean => {
      const isLatestVersion = config.version === latestVersion;
      const isCurrentlySelected = config.isSelected === true;
      const wasInitiallySelected = config.initialIsSelected === true;
      const hasSavedUrl = Boolean(config.initialValue ?? config.value);
      return (
        isLatestVersion ||
        isCurrentlySelected ||
        wasInitiallySelected ||
        hasSavedUrl
      );
    };

    const reversedConfigs = [...versionConfigs].reverse();
    if (shouldShowAllVersions) {
      return reversedConfigs;
    }
    return reversedConfigs.filter((config) =>
      isVersionVisible(config, latestVersion)
    );
  }, [versionConfigs, shouldShowAllVersions, latestVersion]);

  const isTestDisabled = useMemo(() => {
    const latestConfig = visibleConfigs.find(
      (config) => config.version === latestVersion
    );
    if (!latestConfig?.value) {
      return true;
    }
    if (testButtonDisabled !== undefined) {
      return testButtonDisabled;
    }
    return false;
  }, [visibleConfigs, latestVersion, testButtonDisabled]);

  const hasMultipleSelectableVersions = useMemo(() => {
    const selectableVersions = visibleConfigs.filter(isVersionSelectable);
    return selectableVersions.length > 1;
  }, [visibleConfigs]);

  const shouldShowRadioButton = useCallback(
    (config: IVersionConfig): boolean => {
      return hasMultipleSelectableVersions && isVersionSelectable(config);
    },
    [hasMultipleSelectableVersions]
  );

  return (
    <SettingsCard
      title={title}
      description={description}
      onSave={onSave}
      isSaveDisabled={isSaveDisabled}
      isLoading={isLoading}
    >
      <div className="flex flex-col gap-6">
        {visibleConfigs.map((config, index) => (
          <React.Fragment key={config.version}>
            <VersionRow
              config={config}
              disabled={disabled}
              isLatest={config.version === latestVersion}
              onTestClick={onTestClick}
              testButtonDisabled={isTestDisabled}
              shouldShowRadioButton={shouldShowRadioButton(config)}
            />
            {index < visibleConfigs.length - 1 && (
              <hr className="border-t border-base-border" />
            )}
          </React.Fragment>
        ))}
        {children}
      </div>
    </SettingsCard>
  );
};
